import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Button, Space, notification } from 'antd';
import { AlertFilled } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import {
  EMERGENCY_INCIDENT_STATES,
  type EmergencyIncident,
  type EmergencyTimelineEntry,
} from '@src/models/interfaces/emergency.interface';
import { AUDIT_DATE_FORMAT, DateUtil } from '@src/packages/utils/dates';
import { EmergencyIncidentService } from '@src/services/EmergencyIncidentService';

import { useEmergencyAccess } from '../hooks/useEmergencyAccess';
import { useEmergencyAlerts } from '../hooks/useEmergencyAlerts';
import { useEmergencyMessageAlerts } from '../hooks/useEmergencyMessageAlerts';
import { PATHS } from '../constants';
import {
  filterByDivisionScope,
  getEmergencyClosureOutcomeLabel,
  isEmergencyCommandCenterPath,
} from '../utils/emergencyPresentation';
import styles from '../styles.module.less';

const NEW_INCIDENTS_NOTIFICATION_KEY = 'global-emergency-new-incidents';
const CLOSED_INCIDENT_NOTIFICATION_PREFIX = 'global-emergency-closed-';

interface GlobalEmergencyAlertsProps {
  children: ReactNode;
}

type EmergencyAlertControls = ReturnType<typeof useEmergencyAlerts>;

const EmergencyAlertContext = createContext<EmergencyAlertControls | null>(null);

export const useGlobalEmergencyAlertControls = (): EmergencyAlertControls => {
  const context = useContext(EmergencyAlertContext);
  if (!context) throw new Error('Emergency alerts must be used within GlobalEmergencyAlerts');
  return context;
};

const ALERT_REASON_IDS: Record<string, string> = {
  manualPanic: 'emergencies.alert.reason.manualPanic',
  safetyCheck: 'emergencies.alert.reason.safetyCheck',
  external: 'emergencies.alert.reason.external',
};

const ALERT_ICON = <AlertFilled style={{ color: '#ff4d4f' }} />;

const ALERT_ROW_STYLE: React.CSSProperties = { display: 'flex', gap: 8, lineHeight: 1.45 };
const ALERT_LABEL_STYLE: React.CSSProperties = { flex: '0 0 76px', opacity: 0.65 };

const AlertRow: React.FC<{ label: ReactNode; children: ReactNode }> = ({ label, children }) => (
  <div style={ALERT_ROW_STYLE}>
    <span style={ALERT_LABEL_STYLE}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
  </div>
);

const GlobalEmergencyAlerts: React.FC<GlobalEmergencyAlertsProps> = ({ children }) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const unreadMessageCountsRef = useRef(new Map<string, number>());
  const activeIncidentSnapshotRef = useRef<{ scopeKey: string; ids: Set<string> } | null>(null);
  const closedNotificationKeysRef = useRef(new Set<string>());
  const pendingClosedIncidentKeysRef = useRef(new Set<string>());
  const access = useEmergencyAccess();
  const serviceRef = useRef<EmergencyIncidentService | null>(null);
  if (serviceRef.current === null) serviceRef.current = new EmergencyIncidentService();

  const [incidents, setIncidents] = useState<EmergencyIncident[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const scopeKey = useMemo(
    () => JSON.stringify([access.organizationId, access.selectedDivisionIds]),
    [access.organizationId, access.selectedDivisionIds],
  );
  const currentScopeKeyRef = useRef(scopeKey);
  const loadedScopeKeyRef = useRef<string | null>(null);
  currentScopeKeyRef.current = scopeKey;
  const scopedIncidents = useMemo(
    () => filterByDivisionScope(incidents, access.divisionFilterIds),
    [access.divisionFilterIds, incidents],
  );

  const openIncident = useCallback(
    (incidentId: string, messageId?: string) => {
      history.push(
        `/${access.organizationId}/-/${PATHS.COMMAND_CENTER}/${incidentId}`,
        messageId ? { emergencyMessageId: messageId } : undefined,
      );
    },
    [access.organizationId, history],
  );

  useEffect(() => {
    if (
      !access.organizationId ||
      !access.hasCommandCenterAccess ||
      (!access.hasOrganizationWideReadAccess && access.selectedDivisionIds.length === 0)
    ) {
      setIncidents([]);
      setHasLoadedOnce(false);
      return undefined;
    }

    setHasLoadedOnce(false);
    // A new scope must re-baseline from its own first snapshot; the stale
    // incidents of the previous scope would otherwise look like closures.
    activeIncidentSnapshotRef.current = null;
    const subscribedScopeKey = scopeKey;
    return serviceRef.current!.subscribeActive(
      access.organizationId,
      access.selectedDivisionIds,
      access.hasOrganizationWideReadAccess,
      nextIncidents => {
        loadedScopeKeyRef.current = subscribedScopeKey;
        setIncidents(nextIncidents);
        setHasLoadedOnce(true);
      },
      () => setHasLoadedOnce(true),
    );
  }, [
    access.hasCommandCenterAccess,
    access.hasOrganizationWideReadAccess,
    access.organizationId,
    access.selectedDivisionIds,
    scopeKey,
  ]);

  useEffect(() => {
    // Ignore the stale incidents still in state right after a scope change.
    if (!hasLoadedOnce || loadedScopeKeyRef.current !== scopeKey) return;

    const activeIds = new Set(
      scopedIncidents
        .filter(incident => incident.state === EMERGENCY_INCIDENT_STATES.ACTIVE)
        .map(incident => incident.id),
    );
    const previous = activeIncidentSnapshotRef.current;
    activeIncidentSnapshotRef.current = { scopeKey, ids: activeIds };

    if (!previous || previous.scopeKey !== scopeKey || !access.organizationId) return;

    const closedIds = Array.from(previous.ids).filter(id => !activeIds.has(id));
    closedIds.forEach(incidentId => {
      const notificationKey = `${CLOSED_INCIDENT_NOTIFICATION_PREFIX}${access.organizationId}-${incidentId}`;
      if (
        closedNotificationKeysRef.current.has(notificationKey) ||
        pendingClosedIncidentKeysRef.current.has(notificationKey)
      ) {
        return;
      }
      pendingClosedIncidentKeysRef.current.add(notificationKey);

      void serviceRef.current!
        .get(
          access.organizationId,
          incidentId,
          access.selectedDivisionIds,
          access.hasOrganizationWideReadAccess,
        )
        .then(incident => {
          if (
            currentScopeKeyRef.current !== scopeKey ||
            !incident ||
            incident.state === EMERGENCY_INCIDENT_STATES.ACTIVE
          ) {
            return;
          }

          closedNotificationKeysRef.current.add(notificationKey);
          const subject =
            incident.subject.displayName ||
            intl.formatMessage({ id: 'emergencies.participant.unknownWorker' });
          const unknownValue = intl.formatMessage({ id: 'emergencies.closure.unavailable' });
          const closure = incident.closure;
          const closedAt = closure?.closedAt
            ? DateUtil.toDateString(closure.closedAt, AUDIT_DATE_FORMAT)
            : unknownValue;
          const closedBy = closure?.closedBy || unknownValue;
          const reason = closure?.reason || unknownValue;
          const outcome = getEmergencyClosureOutcomeLabel(closure?.outcome);
          const summary = closure?.summary || unknownValue;

          notification.open({
            key: notificationKey,
            className: styles.emergencyNotification,
            icon: ALERT_ICON,
            message: intl.formatMessage({ id: 'emergencies.closureAlert.title' }, { subject }),
            description: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <AlertRow label={intl.formatMessage({ id: 'emergencies.alert.field.emergency' })}>
                  {incident.emergencyTypeName || unknownValue}
                </AlertRow>
                <AlertRow label={intl.formatMessage({ id: 'emergencies.closure.status' })}>
                  {intl.formatMessage({
                    id:
                      incident.state === EMERGENCY_INCIDENT_STATES.RESOLVED
                        ? 'emergencies.state.resolved'
                        : 'emergencies.state.canceledFalseAlarm',
                  })}
                </AlertRow>
                <AlertRow label={intl.formatMessage({ id: 'emergencies.field.endedAt' })}>
                  {closedAt}
                </AlertRow>
                <AlertRow label={intl.formatMessage({ id: 'emergencies.closure.closedBy' })}>
                  {closedBy}
                </AlertRow>
                <AlertRow label={intl.formatMessage({ id: 'emergencies.closure.reason' })}>
                  {reason}
                </AlertRow>
                {outcome && (
                  <AlertRow label={intl.formatMessage({ id: 'emergencies.closure.outcome' })}>
                    {outcome}
                  </AlertRow>
                )}
                {closure?.summary && (
                  <AlertRow label={intl.formatMessage({ id: 'emergencies.closure.summary' })}>
                    {summary}
                  </AlertRow>
                )}
              </div>
            ),
            placement: 'topRight',
            duration: 0,
          });
        })
        .catch(() => undefined)
        .finally(() => pendingClosedIncidentKeysRef.current.delete(notificationKey));
    });
  }, [
    access.hasOrganizationWideReadAccess,
    access.organizationId,
    access.selectedDivisionIds,
    hasLoadedOnce,
    intl,
    scopeKey,
    scopedIncidents,
  ]);

  const alertMessages = useMemo(
    () => ({
      notificationTitle: intl.formatMessage({ id: 'emergencies.alert.notificationTitle' }),
      notificationBody: (incident: EmergencyIncident) =>
        intl.formatMessage(
          { id: 'emergencies.alert.notificationBody' },
          {
            subject:
              incident.subject.displayName ||
              intl.formatMessage({ id: 'emergencies.participant.unknownWorker' }),
            type: incident.emergencyTypeName,
            reason: intl.formatMessage({
              id: ALERT_REASON_IDS[incident.activationMethod] ?? ALERT_REASON_IDS.external,
            }),
          },
        ),
      titleFlash: (count: number) =>
        intl.formatMessage({ id: 'emergencies.alert.titleFlash' }, { count }),
    }),
    [intl],
  );

  const alerts = useEmergencyAlerts({
    incidents: scopedIncidents,
    enabled: hasLoadedOnce,
    resetKey: scopeKey,
    messages: alertMessages,
    onOpenIncident: openIncident,
  });
  const {
    acknowledge,
    muted,
    notificationPermission,
    requestNotificationPermission,
    toggleMute,
    unacknowledgedIds,
  } = alerts;

  const messageAlertMessages = useMemo(
    () => ({
      title: intl.formatMessage({ id: 'emergencies.messageAlert.title' }),
      titleFor: (incident: EmergencyIncident) =>
        `${incident.emergencyTypeName} - ${
          incident.subject.displayName ||
          intl.formatMessage({ id: 'emergencies.participant.unknownWorker' })
        }`,
      body: (incident: EmergencyIncident, entry: EmergencyTimelineEntry) => {
        const subject =
          incident.subject.displayName ||
          intl.formatMessage({ id: 'emergencies.participant.unknownWorker' });
        const author =
          entry.actorName || intl.formatMessage({ id: 'emergencies.participant.system' });
        return intl.formatMessage(
          { id: 'emergencies.messageAlert.body' },
          { author, subject, type: incident.emergencyTypeName, message: entry.message },
        );
      },
    }),
    [intl],
  );

  useEmergencyMessageAlerts({
    incidents: scopedIncidents,
    organizationId: access.organizationId,
    enabled: hasLoadedOnce,
    resetKey: scopeKey,
    muted: alerts.muted,
    notificationPermission: alerts.notificationPermission,
    messages: messageAlertMessages,
    onOpenMessages: openIncident,
    onIncomingMessage: (incident, entry) => {
      // The command center already shows incoming messages live.
      if (isEmergencyCommandCenterPath(location.pathname)) return;
      // One card per incident: further messages update it instead of stacking.
      const messageKey = `global-emergency-messages-${incident.id}`;
      const count = (unreadMessageCountsRef.current.get(incident.id) ?? 0) + 1;
      unreadMessageCountsRef.current.set(incident.id, count);
      const dismiss = () => {
        unreadMessageCountsRef.current.delete(incident.id);
        notification.close(messageKey);
      };
      const author =
        entry.actorName || intl.formatMessage({ id: 'emergencies.participant.system' });
      notification.open({
        key: messageKey,
        className: styles.emergencyNotification,
        icon: ALERT_ICON,
        message: messageAlertMessages.titleFor(incident),
        onClose: () => unreadMessageCountsRef.current.delete(incident.id),
        btn: (
          <Button
            block
            type="primary"
            onClick={() => {
              dismiss();
              openIncident(incident.id, entry.id);
            }}
          >
            {intl.formatMessage({ id: 'emergencies.messageAlert.open' })}
          </Button>
        ),
        description: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <strong>
              {intl.formatMessage({ id: 'emergencies.messageAlert.count' }, { count })}
            </strong>
            <AlertRow label={intl.formatMessage({ id: 'emergencies.messageAlert.from' })}>
              <strong>{author}</strong>
            </AlertRow>
            <AlertRow label={intl.formatMessage({ id: 'emergencies.messageAlert.message' })}>
              <span
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {entry.message}
              </span>
            </AlertRow>
          </div>
        ),
        placement: 'topRight',
        duration: 0,
      });
    },
  });

  // Entering the command center clears the stacked message cards.
  useEffect(() => {
    if (!isEmergencyCommandCenterPath(location.pathname)) return;
    unreadMessageCountsRef.current.forEach((_count, incidentId) =>
      notification.close(`global-emergency-messages-${incidentId}`),
    );
    unreadMessageCountsRef.current.clear();
  }, [location.pathname]);

  useEffect(() => {
    const ids = unacknowledgedIds;
    if (!hasLoadedOnce || ids.length === 0) {
      notification.close(NEW_INCIDENTS_NOTIFICATION_KEY);
      return;
    }

    const pending = ids
      .map(id => scopedIncidents.find(incident => incident.id === id))
      .filter((incident): incident is EmergencyIncident => Boolean(incident));
    const MAX_LISTED = 3;

    const single = pending.length === 1 && ids.length === 1 ? pending[0] : null;

    notification.open({
      key: NEW_INCIDENTS_NOTIFICATION_KEY,
      className: styles.emergencyNotification,
      icon: ALERT_ICON,
      message: single
        ? messageAlertMessages.titleFor(single)
        : intl.formatMessage({ id: 'emergencies.alert.newIncidents' }, { count: ids.length }),
      description: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pending.slice(0, MAX_LISTED).map(incident => (
            <div key={incident.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!single && (
                <>
                  <AlertRow label={intl.formatMessage({ id: 'emergencies.alert.field.person' })}>
                    <strong>
                      {incident.subject.displayName ||
                        intl.formatMessage({ id: 'emergencies.participant.unknownWorker' })}
                    </strong>
                  </AlertRow>
                  <AlertRow
                    label={intl.formatMessage({ id: 'emergencies.alert.field.emergency' })}
                  >
                    {incident.emergencyTypeName}
                  </AlertRow>
                </>
              )}
              <AlertRow label={intl.formatMessage({ id: 'emergencies.alert.field.reason' })}>
                {intl.formatMessage({
                  id: ALERT_REASON_IDS[incident.activationMethod] ?? ALERT_REASON_IDS.external,
                })}
              </AlertRow>
            </div>
          ))}
          {pending.length > MAX_LISTED && (
            <span style={{ opacity: 0.65 }}>
              {intl.formatMessage(
                { id: 'emergencies.alert.moreIncidents' },
                { count: pending.length - MAX_LISTED },
              )}
            </span>
          )}
        </div>
      ),
      btn: (
        <Space size="small">
          <Button
            type="primary"
            danger
            onClick={() => {
              const incidentId = ids[0];
              acknowledge();
              notification.close(NEW_INCIDENTS_NOTIFICATION_KEY);
              openIncident(incidentId);
            }}
          >
            {intl.formatMessage({ id: 'emergencies.alert.openAndAcknowledge' })}
          </Button>
          <Button onClick={toggleMute}>
            {intl.formatMessage({
              id: muted ? 'emergencies.alert.unmute' : 'emergencies.alert.mute',
            })}
          </Button>
          {notificationPermission === 'default' && (
            <Button onClick={requestNotificationPermission}>
              {intl.formatMessage({ id: 'emergencies.alert.enableNotifications' })}
            </Button>
          )}
        </Space>
      ),
      closeIcon: null,
      placement: 'topRight',
      duration: 0,
    });
  }, [
    acknowledge,
    muted,
    notificationPermission,
    requestNotificationPermission,
    toggleMute,
    unacknowledgedIds,
    scopedIncidents,
    messageAlertMessages,
    intl,
    hasLoadedOnce,
    openIncident,
  ]);

  useEffect(
    () => () => notification.close(NEW_INCIDENTS_NOTIFICATION_KEY),
    [],
  );

  return <EmergencyAlertContext.Provider value={alerts}>{children}</EmergencyAlertContext.Provider>;
};

export default GlobalEmergencyAlerts;
